import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Inbox, Loader2, Mail, Phone, Building2 } from "lucide-react";
import { LOGO_ICON } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "scheduled", "closed"];
const statusColor = {
  new: "bg-primary/10 text-primary",
  contacted: "bg-amber-100 text-amber-700",
  scheduled: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-200 text-slate-600",
};

export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("cpd_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/leads`, auth);
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("cpd_token");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) { navigate("/admin"); return; }
    load();
    // eslint-disable-next-line
  }, []);

  const updateStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    await axios.patch(`${API}/leads/${id}`, { status }, auth);
  };

  const logout = () => {
    localStorage.removeItem("cpd_token");
    navigate("/admin");
  };

  const newCount = leads.filter((l) => l.status === "new").length;

  return (
    <div className="min-h-screen bg-muted">
      <header className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_ICON} alt="Logo" className="h-9 w-9 object-contain" />
            <span className="font-heading text-lg text-secondary">Leads Dashboard</span>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold text-secondary hover:text-primary transition-colors duration-200">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
        <div className="grid sm:grid-cols-3 gap-5 mb-10">
          <div className="bg-white border border-border rounded-2xl p-6">
            <p className="text-xs uppercase tracking-wide font-bold text-muted-foreground mb-2">Total Requests</p>
            <p data-testid="stat-total" className="font-heading text-4xl text-secondary">{leads.length}</p>
          </div>
          <div className="bg-white border border-border rounded-2xl p-6">
            <p className="text-xs uppercase tracking-wide font-bold text-muted-foreground mb-2">New</p>
            <p data-testid="stat-new" className="font-heading text-4xl text-primary">{newCount}</p>
          </div>
          <div className="bg-white border border-border rounded-2xl p-6">
            <p className="text-xs uppercase tracking-wide font-bold text-muted-foreground mb-2">Scheduled</p>
            <p className="font-heading text-4xl text-emerald-600">{leads.filter((l) => l.status === "scheduled").length}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" size={32} /></div>
        ) : leads.length === 0 ? (
          <div data-testid="no-leads" className="text-center py-20 bg-white rounded-2xl border border-border">
            <Inbox className="mx-auto text-muted-foreground mb-4" size={48} />
            <p className="text-muted-foreground">No service requests yet.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="leads-list">
            {leads.map((l) => (
              <div key={l.id} data-testid={`lead-${l.id}`} className="bg-white border border-border rounded-2xl p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-heading text-xl text-secondary">{l.name}</h3>
                      <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${statusColor[l.status] || statusColor.new}`}>{l.status}</span>
                    </div>
                    {l.organization && <p className="text-sm text-muted-foreground flex items-center gap-1.5"><Building2 size={14} /> {l.organization}</p>}
                  </div>
                  <p className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</p>
                </div>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 text-primary hover:underline"><Mail size={14} /> {l.email}</a>
                  <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 text-primary hover:underline"><Phone size={14} /> {l.phone}</a>
                </div>
                {l.service_type && <p className="mt-3 text-sm"><span className="font-semibold text-secondary">Service:</span> <span className="text-muted-foreground">{l.service_type}</span></p>}
                {l.message && <p className="mt-2 text-sm text-muted-foreground bg-muted rounded-lg p-3">{l.message}</p>}
                <div className="mt-4 flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      data-testid={`status-${s}-${l.id}`}
                      onClick={() => updateStatus(l.id, s)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors duration-200 ${l.status === s ? "bg-secondary text-white border-secondary" : "border-border text-secondary/70 hover:border-primary hover:text-primary"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
